import type { EpisodeAvailability, TitleBrief } from "@anisync/types";
import { ArrowLeftIcon, CheckIcon, PlusIcon, SearchIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { getEpisodes, searchTitles } from "../lib/api";
import { isEpisodeQueueable } from "./room-queue-rules";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { LoadingNote, SLOW_SOURCES_LABEL } from "./ui/loading";
import { Skeleton } from "./ui/skeleton";

/**
 * Dokładanie odcinków do kolejki bez wychodzenia z pokoju.
 *
 * Wcześniej jedyną drogą było przejście na stronę tytułu — czyli **opuszczenie
 * pokoju w trakcie seansu**: odtwarzanie się urywało, a po powrocie trzeba było
 * czekać na ponowne wejście i synchronizację. Zdarzenie `queue:add` istniało po
 * obu stronach od M5 i nikt go nie wołał; brakowało wyłącznie tego panelu.
 *
 * Dwa kroki, nie jeden: najpierw tytuł, potem odcinek. Wyszukiwanie zwraca
 * tytuły, a nie odcinki, więc jednokrokowy wybór wymagałby wpisywania numeru
 * z pamięci — a numery dostępnych odcinków są dokładnie tym, czego nikt nie zna.
 */

/** Odstęp przed wysłaniem zapytania. Mniej znaczy żądanie na każdą literę. */
const DEBOUNCE_MS = 350;

/** Stałe klucze zastępczych numerów odcinków — indeks tablicy nie jest kluczem. */
const NUMBER_SKELETON_KEYS = Array.from({ length: 12 }, (_, i) => `n${i + 1}`);

export function RoomQueueAdd({
  onAdd,
}: {
  onAdd: (input: { titleId: string; episodeNumber: number }) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<TitleBrief[] | null>(null);
  const [picked, setPicked] = useState<TitleBrief | null>(null);
  const [episodes, setEpisodes] = useState<EpisodeAvailability[] | null>(null);
  const [added, setAdded] = useState<number[]>([]);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2 || picked !== null) {
      setResults(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      searchTitles(trimmed, 8)
        .then((found) => {
          if (!cancelled) setResults(found);
        })
        // Nieudane wyszukiwanie nie może wywalić pokoju — panel jest dodatkiem
        // do oglądania, nie jego warunkiem.
        .catch(() => setResults([]));
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, picked]);

  useEffect(() => {
    if (picked === null) {
      setEpisodes(null);
      return;
    }
    let cancelled = false;
    setAdded([]);
    getEpisodes(picked.id)
      .then((list) => {
        if (!cancelled) setEpisodes(list);
      })
      .catch(() => setEpisodes([]));
    return () => {
      cancelled = true;
    };
  }, [picked]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold tracking-wide text-muted-foreground uppercase">
          Dodaj do kolejki
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-2">
        {picked === null ? (
          <>
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Tytuł anime"
                className="pl-9"
                aria-label="Szukaj tytułu do kolejki"
              />
            </div>

            {results !== null && results.length === 0 && (
              <p className="text-xs text-muted-foreground">Brak wyników.</p>
            )}

            <ul className="flex flex-col">
              {(results ?? []).map((title) => (
                <li key={title.id}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start text-left"
                    onClick={() => setPicked(title)}
                  >
                    <span className="truncate">{title.titleEnglish ?? title.titleRomaji}</span>
                  </Button>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPicked(null)}
                aria-label="Wróć do wyszukiwania"
              >
                <ArrowLeftIcon />
              </Button>
              <span className="min-w-0 truncate text-sm font-medium">
                {picked.titleEnglish ?? picked.titleRomaji}
              </span>
            </div>

            {/* Kształt siatki numerów na czas pytania źródeł — przy Shinden to
                bywa pół minuty, a nieruchomy napis wyglądał jak zawieszenie. */}
            {episodes === null && (
              <>
                <LoadingNote
                  label="Sprawdzam źródła…"
                  slowLabel={SLOW_SOURCES_LABEL}
                  className="text-xs"
                />
                <div
                  aria-hidden="true"
                  className="grid grid-cols-[repeat(auto-fill,minmax(3rem,1fr))] gap-1"
                >
                  {NUMBER_SKELETON_KEYS.map((key) => (
                    <Skeleton key={key} className="h-8" />
                  ))}
                </div>
              </>
            )}
            {episodes !== null && episodes.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Żadne źródło nie ma tego tytułu. Sprawdź mapowania w ustawieniach źródeł.
              </p>
            )}

            {/*
              Numery odcinków siatką, nie polem tekstowym: dostępność zależy od
              źródeł, więc wpisanie numeru z pamięci kończy się prośbą o odcinek,
              którego nikt nie ma.
            */}
            <div className="grid grid-cols-[repeat(auto-fill,minmax(3rem,1fr))] gap-1">
              {(episodes ?? []).map((ep) => {
                const already = added.includes(ep.episodeNumber);
                /**
                 * Odcinek, którego nie ma ŻADNE źródło i którego nie mamy na
                 * dysku, jest nieklikalny.
                 *
                 * Siatka dostaje z serwera także numery dopisane do deklarowanej
                 * długości serii — luki w wydaniach są tam widoczne właśnie po
                 * pustej liście źródeł. Bez tego warunku panel zapraszał do
                 * zakolejkowania czegoś, co kończy się tabelą nieudanych prób
                 * i pozycją, której pokój nigdy nie odtworzy.
                 */
                const unavailable = !isEpisodeQueueable(ep);
                return (
                  <Button
                    key={ep.episodeNumber}
                    variant={already ? "secondary" : "outline"}
                    size="sm"
                    className="tabular-nums"
                    disabled={unavailable}
                    title={unavailable ? "Żadne źródło nie ma tego odcinka" : undefined}
                    onClick={() => {
                      onAdd({ titleId: picked.id, episodeNumber: ep.episodeNumber });
                      // Ptaszek zostaje, bo kolejka wraca zdarzeniem z serwera
                      // z opóźnieniem — bez tego nie wiadomo, czy kliknięcie weszło.
                      setAdded((prev) => [...prev, ep.episodeNumber]);
                    }}
                  >
                    {already ? <CheckIcon /> : <PlusIcon />}
                    {ep.episodeNumber}
                  </Button>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
